"use client";

import { useCallback, useEffect, useState } from "react";
import { isBridgeMessage } from "./core";
import {
  AppNavigatePayload,
  AppShowToastPayload,
  BRIDGE_VERSION,
  CustomEventPayload,
  WSContext,
  WSEventType,
  WSMessage,
} from "./types";

type AppMessageHandler = (msg: WSMessage, event: MessageEvent) => void;

export interface UseAppBridgeOptions {
  appId: number;
  appVersion?: string;
  userId?: string; // User ID from token for validation
  workspaceId?: string; // Workspace ID from token for validation
  orgId?: string; // Organization ID from token for validation
  debug?: boolean; // Enable debug logging
  onMessage?: AppMessageHandler;
  onContextUpdate?: (ctx: WSContext) => void;
  onHello?: (ctx: WSContext) => void; // WS_HELLO or ORG_HELLO
  onLanguageChanged?: (locale: string) => void; // WS_LANGUAGE_CHANGED or ORG_LANGUAGE_CHANGED
  onWorkspaceChanged?: (workspaceId: string, workspaceName?: string) => void; // WS_WORKSPACE_CHANGED or ORG_WORKSPACE_CHANGED
  onSessionExpired?: () => void; // WS_SESSION_EXPIRED or ORG_SESSION_EXPIRED
  onAppNavigate?: (route: string, params?: Record<string, any>) => void; // WS_APP_NAVIGATE or ORG_APP_NAVIGATE
  onCustomEvent?: (payload: CustomEventPayload) => void; // WS_CUSTOM_EVENT or ORG_CUSTOM_EVENT
}

export interface UseAppBridgeReturn {
  context: WSContext | null;
  postToParent: <T>(type: WSEventType | string, payload?: T) => void;
  sendHello: () => void;
  sendReady: (initialRoute?: string, metadata?: Record<string, any>) => void;
  requestContext: () => void;
  showToast: (payload: AppShowToastPayload) => void;
  navigate: (payload: AppNavigatePayload) => void;
  sendCustomEvent: (payload: CustomEventPayload) => void;
}

export function useAppBridge(options: UseAppBridgeOptions) {
  const {
    appId,
    appVersion,
    userId,
    workspaceId,
    orgId,
    debug = false,
    onMessage,
    onContextUpdate,
    onHello,
    onLanguageChanged,
    onWorkspaceChanged,
    onSessionExpired,
    onAppNavigate,
    onCustomEvent,
  } = options;
  const [context, setContext] = useState<WSContext | null>(null);

  // DEBUG: Listen to ALL raw postMessage events when debug=true
  useEffect(() => {
    if (!debug) return;

    console.log(
      "[useAppBridge] 🔍 Debug mode enabled - listening to ALL postMessage events"
    );
    const debugHandler = (event: MessageEvent) => {
      console.log("[useAppBridge] 🔴 Raw postMessage received:", {
        origin: event.origin,
        data: event.data,
      });
    };
    window.addEventListener("message", debugHandler);
    return () => window.removeEventListener("message", debugHandler);
  }, [debug]);

  // Lắng message từ workspace hoặc organization
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handler = (event: MessageEvent) => {
      const data = event.data;
      if (!isBridgeMessage(data)) return;
      if (data.from !== "workspace" && data.from !== "organization") return;

      // Handle WS_HELLO or ORG_HELLO
      if (
        data.type === WSEventType.WS_HELLO ||
        data.type === WSEventType.ORG_HELLO
      ) {
        setContext(data.payload as WSContext);
        onHello?.(data.payload as WSContext);
      }

      // Handle WS_CONTEXT_UPDATE or ORG_CONTEXT_UPDATE
      if (
        data.type === WSEventType.WS_CONTEXT_UPDATE ||
        data.type === WSEventType.ORG_CONTEXT_UPDATE
      ) {
        setContext(data.payload as WSContext);
        onContextUpdate?.(data.payload as WSContext);
      }

      // Handle WS_LANGUAGE_CHANGED or ORG_LANGUAGE_CHANGED
      if (
        data.type === WSEventType.WS_LANGUAGE_CHANGED ||
        data.type === WSEventType.ORG_LANGUAGE_CHANGED
      ) {
        onLanguageChanged?.(data.payload?.locale);
      }

      // Handle WS_WORKSPACE_CHANGED or ORG_WORKSPACE_CHANGED
      if (
        data.type === WSEventType.WS_WORKSPACE_CHANGED ||
        data.type === WSEventType.ORG_WORKSPACE_CHANGED
      ) {
        onWorkspaceChanged?.(
          data.payload?.workspace_id,
          data.payload?.workspace_name
        );
      }

      // Handle WS_SESSION_EXPIRED or ORG_SESSION_EXPIRED
      if (
        data.type === WSEventType.WS_SESSION_EXPIRED ||
        data.type === WSEventType.ORG_SESSION_EXPIRED
      ) {
        onSessionExpired?.();
      }

      // Handle WS_APP_NAVIGATE or ORG_APP_NAVIGATE
      if (
        data.type === WSEventType.WS_APP_NAVIGATE ||
        data.type === WSEventType.ORG_APP_NAVIGATE
      ) {
        onAppNavigate?.(data.payload?.route, data.payload?.params);
      }

      // Handle WS_CUSTOM_EVENT or ORG_CUSTOM_EVENT
      if (
        data.type === WSEventType.WS_CUSTOM_EVENT ||
        data.type === WSEventType.ORG_CUSTOM_EVENT
      ) {
        onCustomEvent?.(data.payload as CustomEventPayload);
      }

      onMessage?.(data, event);
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [
    onMessage,
    onContextUpdate,
    onHello,
    onLanguageChanged,
    onWorkspaceChanged,
    onSessionExpired,
    onAppNavigate,
    onCustomEvent,
  ]);

  // Gửi message lên parent (workspace hoặc organization)
  const postToParent = useCallback(
    <T>(type: WSEventType | string, payload?: T) => {
      if (typeof window === "undefined" || !window.parent) return;

      const msg: WSMessage<T> = {
        namespace: "WS_BRIDGE",
        from: "app",
        app_id: appId,
        type,
        version: BRIDGE_VERSION,
        payload,
      };

      // TODO: production nên fix cứng origin
      window.parent.postMessage(msg, "*");
    },
    [appId]
  );

  // tiện ích: gửi APP_HELLO
  const sendHello = useCallback(() => {
    postToParent(WSEventType.APP_HELLO, {
      app_id: appId,
      app_version: appVersion,
      user_id: userId,
      workspace_id: workspaceId,
      org_id: orgId,
    });
  }, [postToParent, appId, appVersion, userId, workspaceId, orgId]);

  // tiện ích: gửi APP_READY
  const sendReady = useCallback(
    (initialRoute?: string, metadata?: Record<string, any>) => {
      postToParent(WSEventType.APP_READY, {
        workspace_id: context?.workspace_id,
        initial_route: initialRoute,
        metadata,
      });
    },
    [postToParent, context]
  );

  // tiện ích: xin context (APP_CONTEXT_REQUEST)
  const requestContext = useCallback(() => {
    postToParent(WSEventType.APP_CONTEXT_REQUEST, {
      workspace_id: context?.workspace_id,
    });
  }, [postToParent, context]);

  // tiện ích: show toast
  const showToast = useCallback(
    (payload: AppShowToastPayload) => {
      postToParent<AppShowToastPayload>(WSEventType.APP_SHOW_TOAST, payload);
    },
    [postToParent]
  );

  // tiện ích: navigate
  const navigate = useCallback(
    (payload: AppNavigatePayload) => {
      postToParent<AppNavigatePayload>(WSEventType.APP_NAVIGATE, payload);
    },
    [postToParent]
  );

  // tiện ích: custom event
  const sendCustomEvent = useCallback(
    (payload: CustomEventPayload) => {
      postToParent<CustomEventPayload>(WSEventType.APP_CUSTOM_EVENT, payload);
    },
    [postToParent]
  );

  return {
    context,
    postToParent,
    sendHello,
    sendReady,
    requestContext,
    showToast,
    navigate,
    sendCustomEvent,
  };
}
